import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogContent,
  DialogActions,
  Drawer,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Typography,
  IconButton,
  Tooltip,
} from "@material-ui/core";
import { makeStyles, alpha, lighten, useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import CloseIcon from "@material-ui/icons/Close";
import CheckIcon from "@material-ui/icons/Check";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import ContactAvatar from "../ContactAvatar";
import NewTicketModal from "../NewTicketModal";
import formatPhone from "../../helpers/formatPhone";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";
import { FormDialogHeader, FormSection, useFormDialogStyles } from "../FormDialog";
import { PRIORITY_OPTIONS, dueShortcuts, toLocalInput } from "./taskDates";
import {
  taskDueInput,
  taskDueTime,
  buildDuePayload,
  REMINDER_OPTIONS,
  DEFAULT_REMINDER,
  reminderLabel,
} from "../../helpers/taskDue";

// horários sugeridos (o campo "Outro" aceita qualquer um)
const TIME_SHORTCUTS = ["09:00", "14:00"];

const useStyles = makeStyles((theme) => ({
  drawerPaper: {
    width: 440,
    maxWidth: "100vw",
    display: "flex",
    flexDirection: "column",
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
  },
  drawerBody: {
    flex: 1,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    padding: theme.spacing(2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    ...sidebarScrollbar(theme),
  },
  options: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
  },
  option: {
    height: 32,
    padding: theme.spacing(0, 1.5),
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.primary,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    font: "500 13px Inter, Roboto, sans-serif",
    whiteSpace: "nowrap",
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 1 },
  },
  optionActive: {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    borderColor: theme.palette.text.primary,
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    flexShrink: 0,
  },
  dateTimeRow: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
  },
  twoCols: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: theme.spacing(2),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0, 1fr)", gap: 0 },
  },
  linkCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.25),
    padding: theme.spacing(1.25),
    borderRadius: 10,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.action.hover,
  },
  linkText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  linkName: {
    fontWeight: 600,
    fontSize: 14,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  dealIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.14),
  },
  smallButton: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 8,
    whiteSpace: "nowrap",
  },
  footer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2.5),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
}));

const emptyForm = {
  text: "",
  priority: "medium",
  dueDate: "",
  dueTime: "",
  // minutos antes do horário para avisar no sininho ("" = sem lembrete)
  remindMinutes: String(DEFAULT_REMINDER),
  description: "",
  assigneeId: "",
  contact: null,
  opportunityId: "",
};

/**
 * Formulário de tarefa.
 * - Criação (sem `task`): janela. Pode vir com contato/negócio já vinculados
 *   (chat, ficha do contato, card do CRM).
 * - Edição (`task`): painel lateral, com concluir, excluir e "Conversar".
 *
 * onSaved(task) — chamado a cada tarefa salva (criada ou editada).
 * onClose() — fecha.
 * onDeleted(task), onToggleComplete(task) — opcionais, usados pela tela de Tarefas.
 */
const TaskEditor = ({
  open,
  onClose,
  onSaved,
  task,
  initialText,
  initialContact,
  initialOpportunity,
  onDeleted,
  onToggleComplete,
}) => {
  const classes = useStyles();
  const dialogClasses = useFormDialogStyles();
  const theme = useTheme();
  const history = useHistory();
  const { user } = useContext(AuthContext);
  const tone = (c) => (theme.palette.type === "dark" ? lighten(c, 0.3) : c);
  const editing = Boolean(task);

  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [contactOptions, setContactOptions] = useState([]);
  const [contactSearch, setContactSearch] = useState("");
  const [searching, setSearching] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [createAnother, setCreateAnother] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    api
      .get("/users/list")
      .then(({ data }) => setUsers(Array.isArray(data) ? data : data?.users || []))
      .catch(() => {});
    if (task) {
      setForm({
        text: task.text || "",
        priority: task.priority || "medium",
        dueDate: taskDueInput(task),
        dueTime: taskDueTime(task),
        remindMinutes:
          task.remindMinutesBefore === null || task.remindMinutesBefore === undefined
            ? ""
            : String(task.remindMinutesBefore),
        description: task.description || "",
        assigneeId: task.userId || "",
        contact: task.contact || null,
        opportunityId: task.opportunityId || "",
      });
    } else {
      setForm({
        ...emptyForm,
        text: initialText || "",
        contact: initialContact || initialOpportunity?.contact || null,
        opportunityId: initialOpportunity?.id || "",
        assigneeId: user?.id || "",
      });
    }
    setContactSearch("");
  }, [open, task, initialText, initialContact, initialOpportunity, user]);

  // busca de contato para vincular
  useEffect(() => {
    if (!open || contactSearch.length < 2) return;
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("contacts", { params: { searchParam: contactSearch } });
        setContactOptions(data.contacts || []);
      } catch (err) {
        toastError(err);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [contactSearch, open]);

  // negócios do contato escolhido (para vincular)
  const contactId = form.contact?.id || null;
  useEffect(() => {
    if (!open || !contactId) {
      setOpportunities([]);
      return;
    }
    api
      .get("/opportunities", { params: { contactId } })
      .then(({ data }) => setOpportunities(data.opportunities || []))
      .catch(() => setOpportunities([]));
  }, [open, contactId]);

  // o negócio vinculado pode não estar na lista (ex.: veio do CRM antes do carregamento)
  const opportunityOptions = useMemo(() => {
    const list = opportunities.slice();
    const current = task?.opportunity || initialOpportunity;
    if (current?.id && !list.some((o) => o.id === current.id)) list.unshift(current);
    return list;
  }, [opportunities, task, initialOpportunity]);

  const selectedOpportunity = opportunityOptions.find((o) => o.id === form.opportunityId);
  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const shortcuts = dueShortcuts();

  // opções de lembrete (inclui um valor salvo fora da lista, se houver)
  const reminderChoices = useMemo(() => {
    const list = REMINDER_OPTIONS.map(String);
    if (form.remindMinutes !== "" && !list.includes(form.remindMinutes)) {
      list.push(form.remindMinutes);
      list.sort((a, b) => Number(a) - Number(b));
    }
    return list;
  }, [form.remindMinutes]);

  const handleSave = async () => {
    const text = form.text.trim();
    if (!text) {
      toast.error("Descreva o que precisa ser feito.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        text,
        priority: form.priority,
        // só data = dia todo; data + horário = hora exata
        dueDate: buildDuePayload(form.dueDate, form.dueTime),
        description: form.description.trim() || null,
        // lembrete só existe para tarefa com horário
        remindMinutesBefore:
          form.dueDate && form.dueTime && form.remindMinutes !== "" ? Number(form.remindMinutes) : null,
        assigneeId: form.assigneeId || undefined,
        contactId: form.contact?.id || null,
        opportunityId: form.opportunityId || null,
      };
      if (editing) {
        const { data } = await api.put(`/tasks/${task.id}`, payload);
        toast.success("Tarefa atualizada.");
        if (onSaved) onSaved(data);
        onClose();
      } else {
        const { data } = await api.post("/tasks", payload);
        const assignedToOther = form.assigneeId && form.assigneeId !== user?.id;
        toast.success(
          assignedToOther
            ? `Tarefa criada para ${users.find((u) => u.id === form.assigneeId)?.name || "o responsável"}.`
            : "Tarefa criada."
        );
        if (onSaved) onSaved(data);
        if (createAnother) {
          // mantém vínculos e responsável; limpa só o texto
          setForm((prev) => ({ ...prev, text: "" }));
        } else {
          onClose();
        }
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const fields = (
    <>
      <TextField
        label="O que precisa ser feito?"
        required
        value={form.text}
        onChange={(e) => setField("text", e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && !editing) {
            e.preventDefault();
            handleSave();
          }
        }}
        placeholder="Ex.: Ligar para confirmar a proposta"
        variant="outlined"
        margin="dense"
        fullWidth
        multiline
        maxRows={4}
        autoFocus={!editing}
        InputLabelProps={{ shrink: true }}
      />

      <FormSection title="Prazo">
        <div className={classes.options} role="group" aria-label="Atalhos de prazo">
          {shortcuts.map((s) => {
            const active = form.dueDate === s.value;
            return (
              <button
                key={s.key}
                type="button"
                className={`${classes.option} ${active ? classes.optionActive : ""}`}
                aria-pressed={active}
                onClick={() =>
                  setForm((prev) => ({ ...prev, dueDate: s.value, dueTime: s.value ? prev.dueTime : "" }))
                }
              >
                {s.label}
              </button>
            );
          })}
        </div>
        <div className={classes.dateTimeRow}>
          <TextField
            type="date"
            label="Data"
            value={form.dueDate}
            onChange={(e) => {
              // lê o valor antes: no React 16 o evento é reaproveitado e
              // e.target vira null quando o setForm roda depois
              // não apaga o horário: digitando pelo teclado o campo fica
              // vazio por um instante. Sem data, o horário é ignorado ao salvar.
              const value = e.target.value;
              setForm((prev) => ({ ...prev, dueDate: value }));
            }}
            variant="outlined"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            style={{ width: 200 }}
          />
          <TextField
            type="time"
            label="Horário (opcional)"
            value={form.dueTime}
            onChange={(e) => {
              const value = e.target.value;
              setForm((prev) => ({
                ...prev,
                dueTime: value,
                // horário sem data = hoje
                dueDate: prev.dueDate || (value ? toLocalInput(new Date()) : ""),
              }));
            }}
            variant="outlined"
            margin="dense"
            InputLabelProps={{ shrink: true }}
            inputProps={{ step: 300 }}
            style={{ width: 170 }}
          />
        </div>
        <div className={classes.options} role="group" aria-label="Atalhos de horário">
          {["", ...TIME_SHORTCUTS].map((time) => {
            const active = form.dueTime === time;
            return (
              <button
                key={time || "none"}
                type="button"
                className={`${classes.option} ${active ? classes.optionActive : ""}`}
                aria-pressed={active}
                onClick={() =>
                  setForm((prev) => ({
                    ...prev,
                    dueTime: time,
                    dueDate: prev.dueDate || (time ? toLocalInput(new Date()) : ""),
                  }))
                }
              >
                {time || "Dia todo"}
              </button>
            );
          })}
        </div>
        <span className={classes.muted}>
          {!form.dueDate
            ? "Sem prazo."
            : form.dueTime
            ? `Vence em ${form.dueDate.split("-").reverse().join("/")} às ${form.dueTime}.`
            : `Vale para o dia todo em ${form.dueDate.split("-").reverse().join("/")}.`}
        </span>
      </FormSection>

      {form.dueDate && form.dueTime && (
        <FormSection title="Lembrete no sininho">
          <div className={classes.options} role="radiogroup" aria-label="Lembrete">
            {["", ...reminderChoices].map((value) => {
              const active = form.remindMinutes === value;
              return (
                <button
                  key={value || "none"}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`${classes.option} ${active ? classes.optionActive : ""}`}
                  onClick={() => setField("remindMinutes", value)}
                >
                  {value === "" ? "Sem lembrete" : reminderLabel(value)}
                </button>
              );
            })}
          </div>
          <span className={classes.muted}>
            {form.remindMinutes === ""
              ? "Não vou avisar."
              : `Aviso ${reminderLabel(form.remindMinutes).toLowerCase()} para ${
                  !form.assigneeId || form.assigneeId === user?.id
                    ? "você"
                    : users.find((u) => u.id === form.assigneeId)?.name || "o responsável"
                }, no sininho do sistema.`}
          </span>
        </FormSection>
      )}

      <div className={classes.twoCols}>
        <FormSection title="Prioridade">
          <div className={classes.options} role="radiogroup" aria-label="Prioridade">
            {PRIORITY_OPTIONS.map((p) => {
              const active = form.priority === p.value;
              return (
                <button
                  key={p.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={classes.option}
                  onClick={() => setField("priority", p.value)}
                  style={
                    active
                      ? { borderColor: p.color, backgroundColor: alpha(p.color, 0.14), color: tone(p.color) }
                      : undefined
                  }
                >
                  <span className={classes.dot} style={{ backgroundColor: p.color }} />
                  {p.label}
                </button>
              );
            })}
          </div>
        </FormSection>
        <FormSection title="Responsável">
          <TextField
            select
            value={form.assigneeId}
            onChange={(e) => setField("assigneeId", e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": "Responsável" }}
          >
            {users.length === 0 && user?.id && <MenuItem value={user.id}>Eu</MenuItem>}
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.id === user?.id ? `${u.name} (você)` : u.name}
              </MenuItem>
            ))}
          </TextField>
        </FormSection>
      </div>

      <FormSection title="Vinculado a">
        {form.contact ? (
          <div className={classes.linkCard}>
            <ContactAvatar
              src={form.contact.profilePicUrl}
              name={form.contact.name}
              style={{ width: 32, height: 32, fontSize: 14 }}
            />
            <div className={classes.linkText}>
              <span className={classes.linkName}>{form.contact.name}</span>
              <span className={classes.muted}>
                {form.contact.number ? formatPhone(form.contact.number) : "Contato"}
              </span>
            </div>
            {editing && form.contact.number && (
              <Button
                size="small"
                color="primary"
                className={classes.smallButton}
                startIcon={<WhatsAppIcon fontSize="small" />}
                onClick={() => setChatOpen(true)}
              >
                Conversar
              </Button>
            )}
            <Tooltip title="Desvincular contato">
              <IconButton
                size="small"
                aria-label="Desvincular contato"
                onClick={() => setForm((prev) => ({ ...prev, contact: null, opportunityId: "" }))}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ) : (
          <Autocomplete
            options={contactOptions}
            loading={searching}
            value={null}
            getOptionLabel={(o) => o.name || ""}
            onChange={(e, value) => value && setForm((prev) => ({ ...prev, contact: value, opportunityId: "" }))}
            onInputChange={(e, value) => setContactSearch(value)}
            noOptionsText={contactSearch.length < 2 ? "Digite pelo menos 2 letras" : "Nenhum contato encontrado"}
            renderOption={(o) => (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <ContactAvatar src={o.profilePicUrl} name={o.name} style={{ width: 28, height: 28, fontSize: 12 }} />
                <span>{o.name}</span>
              </div>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                margin="dense"
                placeholder="Vincular contato (opcional)"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searching ? <CircularProgress color="inherit" size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {form.contact && (
          <TextField
            select
            value={form.opportunityId}
            onChange={(e) => setField("opportunityId", e.target.value)}
            variant="outlined"
            margin="dense"
            fullWidth
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": "Negócio" }}
            helperText={
              opportunityOptions.length === 0 ? "Este contato não tem negócios no CRM." : undefined
            }
          >
            <MenuItem value="">
              <span className={classes.muted} style={{ fontSize: 14 }}>
                Sem negócio vinculado
              </span>
            </MenuItem>
            {opportunityOptions.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.title}
                {o.stage?.name ? ` · ${o.stage.name}` : ""}
              </MenuItem>
            ))}
          </TextField>
        )}

        {editing && selectedOpportunity && (
          <div className={classes.linkCard}>
            <span className={classes.dealIcon}>
              <TrendingUpIcon fontSize="small" />
            </span>
            <div className={classes.linkText}>
              <span className={classes.linkName}>{selectedOpportunity.title}</span>
              <span className={classes.muted}>Negócio no CRM</span>
            </div>
            <Button
              size="small"
              color="primary"
              className={classes.smallButton}
              onClick={() => {
                onClose();
                history.push("/crm");
              }}
            >
              Abrir no CRM
            </Button>
          </div>
        )}
      </FormSection>

      <TextField
        label="Descrição (opcional)"
        value={form.description}
        onChange={(e) => setField("description", e.target.value)}
        placeholder="Detalhes, roteiro da ligação, links…"
        variant="outlined"
        margin="dense"
        fullWidth
        multiline
        minRows={3}
        maxRows={10}
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 5000 }}
      />
    </>
  );

  const saveButton = (
    <Button
      onClick={handleSave}
      color="primary"
      variant="contained"
      disableElevation
      disabled={saving}
      className={`${dialogClasses.actionButton} ${dialogClasses.progressWrapper}`}
    >
      {editing ? "Salvar" : "Criar tarefa"}
      {saving && <CircularProgress size={24} className={dialogClasses.buttonProgress} />}
    </Button>
  );

  const chatModal = form.contact && (
    <NewTicketModal
      modalOpen={chatOpen}
      initialContact={form.contact}
      onClose={(ticket) => {
        setChatOpen(false);
        if (ticket?.uuid) {
          onClose();
          history.push(`/tickets/${ticket.uuid}`);
        }
      }}
    />
  );

  // ---- edição: painel lateral ----
  if (editing) {
    return (
      <>
        {chatModal}
        <Drawer
          anchor="right"
          open={open}
          onClose={() => onClose()}
          classes={{ paper: classes.drawerPaper }}
        >
          <FormDialogHeader
            title={task.completed ? "Tarefa concluída" : "Tarefa"}
            subtitle={[
              task.createdAt && `Criada em ${new Date(task.createdAt).toLocaleDateString("pt-BR")}`,
              task.user?.name && `responsável: ${task.user.name}`,
            ]
              .filter(Boolean)
              .join(" · ")}
            onClose={() => onClose()}
          />
          <div className={classes.drawerBody}>{fields}</div>
          <div className={classes.footer}>
            {onDeleted && (
              <Button
                className={classes.smallButton}
                startIcon={<DeleteOutlineIcon />}
                onClick={() => onDeleted(task)}
                style={{ color: theme.palette.text.secondary }}
              >
                Excluir
              </Button>
            )}
            <span style={{ flex: 1 }} />
            {onToggleComplete && (
              <Button
                variant="outlined"
                className={dialogClasses.actionButton}
                startIcon={<CheckIcon />}
                onClick={() => onToggleComplete(task)}
                style={{ color: tone("#43A047"), borderColor: alpha("#43A047", 0.5) }}
              >
                {task.completed ? "Reabrir" : "Concluir"}
              </Button>
            )}
            {saveButton}
          </div>
        </Drawer>
      </>
    );
  }

  // ---- criação: janela ----
  return (
    <Dialog
      open={open}
      onClose={() => onClose()}
      maxWidth="sm"
      fullWidth
      scroll="paper"
      classes={{ paper: dialogClasses.paper }}
      aria-labelledby="task-editor-title"
    >
      <FormDialogHeader
        id="task-editor-title"
        title="Nova tarefa"
        subtitle={
          initialOpportunity
            ? "O negócio e o contato já vêm vinculados."
            : initialContact
            ? "O contato já vem vinculado."
            : "Defina o que fazer, até quando e para quem."
        }
        onClose={() => onClose()}
        disabled={saving}
      />
      <DialogContent dividers className={dialogClasses.content} style={{ gap: 16 }}>
        {fields}
      </DialogContent>
      <DialogActions className={dialogClasses.actions}>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              color="primary"
              checked={createAnother}
              onChange={(e) => setCreateAnother(e.target.checked)}
            />
          }
          label={<Typography variant="body2">Criar outra em seguida</Typography>}
          style={{ marginRight: "auto" }}
        />
        <Button
          onClick={() => onClose()}
          disabled={saving}
          className={`${dialogClasses.actionButton} ${dialogClasses.cancelButton}`}
        >
          Cancelar
        </Button>
        {saveButton}
      </DialogActions>
    </Dialog>
  );
};

export default TaskEditor;
